import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/SharedAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useCreateTicket } from '@/hooks/useTickets';
import { calculatePriority, FORM_ASSIGNMENTS } from '../../../../../shared/form-config';
import { useTranslation } from 'react-i18next';
import { ReporterInfoFields } from '@/components/tickets/ReporterInfoFields';
import { TicketFormButtons } from '@/components/tickets/TicketFormButtons';

const hardwareBugSchema = z.object({
  summary: z.string().min(5, 'El resumen debe tener al menos 5 caracteres'),
  restartedLaptop: z.enum(['yes', 'no'], {
    required_error: 'Debe indicar si reinició el equipo',
  }),
  restartedPrograms: z.enum(['yes', 'no'], {
    required_error: 'Debe indicar si reinició los programas',
  }),
});

type HardwareBugFormData = z.infer<typeof hardwareBugSchema>;

export function HardwareBugForm() {
  const { user } = useAuth();
  const { t } = useTranslation();

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<HardwareBugFormData>({
    resolver: zodResolver(hardwareBugSchema),
  });

  const restartedLaptop = watch('restartedLaptop');
  const restartedPrograms = watch('restartedPrograms');

  // ✨ Using tRPC mutation
  const createTicketMutation = useCreateTicket();
  const onSubmit = async (data: HardwareBugFormData) => {
    const priority = calculatePriority('hardware-bug', data);
    const ticketData = {
      type: 'hardware-bug',
      title: `Hardware Error: ${data.summary}`,
      formData: {
        reporterName: user?.name,
        reporterEmail: user?.email,
        ...data,
      },
      attachments: [],
      priority,
      assignedTo: FORM_ASSIGNMENTS['hardware-bug'],
    };
    createTicketMutation.mutate(ticketData);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('forms.hardwareBug')}</CardTitle>
        <CardDescription>
          {t('tickets.hardwarebugform.reportar_problema_con_equipo_existente_prioridad')} {FORM_ASSIGNMENTS['hardware-bug']}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <ReporterInfoFields name={user?.name} email={user?.email} />

          <div className="space-y-2">
            <Label htmlFor="summary">
              {t('forms.summary')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="summary"
              placeholder={t('forms.summaryPlaceholder')}
              {...register('summary')}
            />
            {errors.summary && (
              <p className="text-sm text-destructive">{errors.summary.message}</p>
            )}
          </div>

          <div className="space-y-3">
            <Label>
              {t('forms.restartedLaptop')} <span className="text-destructive">*</span>
            </Label>
            <RadioGroup value={restartedLaptop} onValueChange={(value) => setValue('restartedLaptop', value as any)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="laptop-yes" />
                <Label htmlFor="laptop-yes" className="font-normal cursor-pointer">
                  {t('forms.yes')}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="laptop-no" />
                <Label htmlFor="laptop-no" className="font-normal cursor-pointer">
                  {t('forms.no')}
                </Label>
              </div>
            </RadioGroup>
            {errors.restartedLaptop && (
              <p className="text-sm text-destructive">{errors.restartedLaptop.message}</p>
            )}
          </div>

          <div className="space-y-3">
            <Label>
              {t('forms.restartedPrograms')} <span className="text-destructive">*</span>
            </Label>
            <RadioGroup value={restartedPrograms} onValueChange={(value) => setValue('restartedPrograms', value as any)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="programs-yes" />
                <Label htmlFor="programs-yes" className="font-normal cursor-pointer">
                  {t('forms.yes')}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="programs-no" />
                <Label htmlFor="programs-no" className="font-normal cursor-pointer">
                  {t('forms.no')}
                </Label>
              </div>
            </RadioGroup>
            {errors.restartedPrograms && (
              <p className="text-sm text-destructive">{errors.restartedPrograms.message}</p>
            )}
          </div>

          <TicketFormButtons isPending={createTicketMutation.isPending} />
        </form>
      </CardContent>
    </Card>
  );
}
